import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";
import HeroSlider, { HeroSlide } from "@/components/ui/HeroSlider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DeferredInventorySelect as Select, DeferredInventorySelectContent as SelectContent } from "@/components/inventory/DeferredInventorySelect";
import { Search, Grid3X3, List, SlidersHorizontal, ChevronDown, X, Pause, Play, Layers } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "@/lib/router-compat";
import { lazy, Suspense, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { usePagedInventory, type RVUnit, type InventoryFilters } from "@/hooks/useInventory";
import RVCard, { useAutoCycleSetting, AutoCyclePausedProvider } from "@/components/inventory/RVCard";
import HorizontalRowCard from "@/components/inventory/HorizontalRowCard";
import { Skeleton } from "@/components/ui/skeleton";
import { rankSearch } from "@/lib/fuzzySearch";
import { incomingUnitsLast } from "@/lib/inventoryOrdering";
import SearchSuggestDropdown from "@/components/inventory/SearchSuggestDropdown";
import WidgetZone from "@/components/ui/WidgetZone";
import { MASTER_RV_TYPES, collapseRvTypeFamilies, isRvTypeVariant } from "@/data/masterRvTypes";
import { useActiveRvTypes } from "@/hooks/useActiveRvTypes";
import RVExpertBanner from "@/components/inventory/RVExpertBanner";
import InventoryFilterSidebar, { type SidebarSelections } from "@/components/inventory/InventoryFilterSidebar";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { parseList, stringifyList } from "@/lib/inventoryFilterDefs";
import { useRouter } from '@tanstack/react-router';
import { createInventoryLandingIntent, inventoryLandingTop } from '@/lib/inventoryLanding';

import { STATIC_ASSETS } from "@/lib/imageUtils";

const RVExpertWizard = lazy(() => import("@/components/inventory/RVExpertWizard"));

const heroSlides: HeroSlide[] = [
  {
    image: STATIC_ASSETS.heroInventory1920,
    imageMobile: STATIC_ASSETS.heroInventory640,
    title: "RV Inventory",
    subtitle: "Find your perfect RV",
  },
];

// rvTypes is now built dynamically from MASTER_RV_TYPES + active index
const rvTypesStatic = ["All Types", ...MASTER_RV_TYPES.filter((t) => !isRvTypeVariant(t))] as const;

const floorplanFeatures = [
  "All Features", "Bath and a Half", "Bunk Over Cab", "Bunkhouse", "Front Bath",
  "Front Bedroom", "Front Cargo Deck", "Front Entertainment", "Front Kitchen",
  "Front Living", "King Bed", "Kitchen Island", "Lithium Battery", "Loft",
  "Murphy Bed", "Outdoor Entertainment", "Outdoor Kitchen", "Rear Bath",
  "Rear Bedroom", "Rear Entertainment", "Rear Kitchen", "Rear Living Area",
  "Rear Twin", "Side Ramp", "Solar Panels", "Tankless Water Heater",
  "Theater Seating", "Two Entry/Exit Doors", "Two Full Baths",
  "U Shaped Dinette", "V-Nose", "Walk-Thru Bath", "Wheelchair Accessible",
];

const sortOptions = [
  { value: "featured", label: "Featured" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "year-new", label: "Year: Newest" },
  { value: "length", label: "Length: Longest" },
  { value: "weight", label: "Weight: Lightest" },
];

const floorplanFeatureSlugs = new Set(
  floorplanFeatures.map((f) => f.toLowerCase().replace(/\s+/g, "-"))
);

function slugToLabel(slug: string, list: string[]): string | undefined {
  return list.find((t) => {
    const s = t.toLowerCase().replace(/\s+/g, "-");
    return s === slug || s.replace(/-{2,}/g, "-") === slug;
  });
}

const Inventory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCondition = searchParams.get("condition");
  const urlSpecial = searchParams.get("special");
  const urlQ = searchParams.get("q") ?? "";
  const urlFeature = searchParams.get("feature");
  const urlRvType = searchParams.get("type");
  const urlWeightMin = searchParams.get("weight_min");
  const urlWeightMax = searchParams.get("weight_max");
  const urlSleepsMin = searchParams.get("sleeps_min");
  const urlSleepsMax = searchParams.get("sleeps_max");
  const urlPaymentMin = searchParams.get("payment_min");
  const urlPaymentMax = searchParams.get("payment_max");
  const urlPriceMin = searchParams.get("price_min");
  const urlPriceMax = searchParams.get("price_max");
  const urlFeatured = searchParams.get("featured");
  const urlDealOfWeek = searchParams.get("deal");
  const urlSort = searchParams.get("sort");
  // Multi-select URL params (comma-joined)
  const urlTypes = searchParams.get("types");
  const urlYears = searchParams.get("years");
  const urlPayments = searchParams.get("payments");
  const urlMfrs = searchParams.get("manufacturers");
  const urlMakes = searchParams.get("makes");
  const urlFeatures = searchParams.get("features");
  const urlWeights = searchParams.get("weights");
  const urlLengths = searchParams.get("lengths");
  const urlSleeps = searchParams.get("sleeps");
  const urlPrices = searchParams.get("prices");
  const urlBeds = searchParams.get("beds");

  const sidebarValues: SidebarSelections = useMemo(() => ({
    types: parseList(urlTypes),
    years: parseList(urlYears),
    paymentBuckets: parseList(urlPayments),
    manufacturers: parseList(urlMfrs),
    makes: parseList(urlMakes),
    features: parseList(urlFeatures),
    weightBuckets: parseList(urlWeights),
    lengthBuckets: parseList(urlLengths),
    sleepsBuckets: parseList(urlSleeps),
    priceBuckets: parseList(urlPrices),
    bedTypes: parseList(urlBeds),
  }), [urlTypes, urlYears, urlPayments, urlMfrs, urlMakes, urlFeatures, urlWeights, urlLengths, urlSleeps, urlPrices, urlBeds]);

  const sidebarUrlKeys: Record<keyof SidebarSelections, string> = {
    types: "types",
    years: "years",
    paymentBuckets: "payments",
    manufacturers: "manufacturers",
    makes: "makes",
    features: "features",
    weightBuckets: "weights",
    lengthBuckets: "lengths",
    sleepsBuckets: "sleeps",
    priceBuckets: "prices",
    bedTypes: "beds",
  };

  const handleSidebarChange = useCallback((key: keyof SidebarSelections, next: string[]) => {
    const params = new URLSearchParams(searchParams);
    const k = sidebarUrlKeys[key];
    if (next.length === 0) params.delete(k);
    else params.set(k, stringifyList(next));
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSidebarClear = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    Object.values(sidebarUrlKeys).forEach((k) => params.delete(k));
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const sidebarSelectedCount = useMemo(
    () => Object.values(sidebarValues).reduce((n, v) => n + v.length, 0),
    [sidebarValues],
  );

  const [wizardOpen, setWizardOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { data: activeTypesIndex } = useActiveRvTypes();

  // Build filtered rvTypes: "All Types" + only master types with active inventory
  const rvTypes = useMemo(() => {
    if (!activeTypesIndex || Object.keys(activeTypesIndex).length === 0) {
      // Fallback: show all types while index loads
      return [...rvTypesStatic];
    }
    return ["All Types", ...collapseRvTypeFamilies(activeTypesIndex)];
  }, [activeTypesIndex]);

  const [searchTerm, setSearchTerm] = useState(urlQ);
  const [debouncedSearch, setDebouncedSearch] = useState(urlQ);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const landingIntent = useRef(createInventoryLandingIntent());

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);
  const [condition, setCondition] = useState(
    urlCondition === "new" ? "new" : urlCondition === "used" ? "used" : "all"
  );
  const [rvType, setRvType] = useState(urlRvType || "all-types");
  const [feature, setFeature] = useState(
    urlFeature && floorplanFeatureSlugs.has(urlFeature)
      ? urlFeature
      : "all-features"
  );
  const [sort, setSort] = useState(urlSort || "featured");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [autoCyclePaused, setAutoCyclePaused] = useState(false);
  const [groupBy, setGroupBy] = useState<"none" | "type" | "make" | "condition">("type");
  const { data: autoCycleSetting } = useAutoCycleSetting();

  const hasActiveFilters = condition !== "all" || rvType !== "all-types" || feature !== "all-features" || searchTerm || urlWeightMin || urlWeightMax || urlSleepsMin || urlSleepsMax || urlPaymentMin || urlPaymentMax || urlPriceMin || urlPriceMax || urlSpecial || urlFeatured || urlDealOfWeek || sidebarSelectedCount > 0;

  const resetSearch = () => {
    setCondition("all");
    setRvType("all-types");
    setFeature("all-features");
    setSearchTerm("");
    setSort("featured");
    navigate("/rv-search", { replace: true });
  };

  // Keep state in sync when navigating via links. Setters are guarded with
  // functional updates so an unchanged value never schedules a re-render.
  useEffect(() => {
    const nextCondition =
      urlCondition === "new" ? "new" : urlCondition === "used" ? "used" : "all";
    setCondition((prev) => (prev === nextCondition ? prev : nextCondition));
    setSearchTerm((prev) => (prev === urlQ ? prev : urlQ));
    const nextFeature =
      urlFeature && floorplanFeatureSlugs.has(urlFeature)
        ? urlFeature
        : "all-features";
    setFeature((prev) => (prev === nextFeature ? prev : nextFeature));
    const match = urlRvType
      ? slugToLabel(urlRvType, rvTypes) ?? slugToLabel(urlRvType, [...MASTER_RV_TYPES])
      : undefined;
    const nextType = match ? match.toLowerCase().replace(/\s+/g, "-") : "all-types";
    setRvType((prev) => (prev === nextType ? prev : nextType));
    const nextSort = urlSort || "featured";
    setSort((prev) => (prev === nextSort ? prev : nextSort));
  }, [urlCondition, urlFeature, urlQ, urlRvType, urlSort, rvTypes]);

  // Sync local filter state → URL so Back/Forward restores the filtered view.
  // writtenRef remembers every query string we've written: if the URL cannot
  // round-trip to the exact string we computed (slug/number re-encoding), the
  // A↔B ping-pong stops after the first write instead of looping forever.
  const writtenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value: string | undefined | null, isDefault: boolean) => {
      if (!isDefault && value) next.set(key, value);
      else next.delete(key);
    };
    setOrDelete("condition", condition, condition === "all");
    setOrDelete("type", rvType, rvType === "all-types");
    setOrDelete("feature", feature, feature === "all-features");
    setOrDelete("q", debouncedSearch, !debouncedSearch);
    setOrDelete("sort", sort, sort === "featured");
    const nextStr = next.toString();
    if (nextStr === searchParams.toString() || writtenRef.current.has(nextStr)) return;
    writtenRef.current.add(nextStr);
    if (debouncedSearch !== urlQ) landingIntent.current.markPreview(Object.fromEntries(next));
    setSearchParams(next, { replace: true });
  }, [condition, rvType, feature, debouncedSearch, sort, searchParams, setSearchParams, urlQ]);

  // Build server-side filters object
  const filters: InventoryFilters = useMemo(() => ({
    condition: condition as "all" | "new" | "used",
    rvType,
    rvTypeLabel: slugToLabel(rvType, rvTypes) ?? slugToLabel(rvType, [...MASTER_RV_TYPES]),
    feature,
    featureLabel: slugToLabel(feature, floorplanFeatures),
    sort,
    featured: urlFeatured === "true",
    special: urlSpecial === "true",
    dealOfWeek: urlSpecial === "deal-of-week" || urlDealOfWeek === "true",
    managerSpecials: urlSpecial === "manager",
    weightMin: urlWeightMin ? Number(urlWeightMin) : undefined,
    weightMax: urlWeightMax ? Number(urlWeightMax) : undefined,
    sleepsMin: urlSleepsMin ? Number(urlSleepsMin) : undefined,
    sleepsMax: urlSleepsMax ? Number(urlSleepsMax) : undefined,
    paymentMin: urlPaymentMin ? Number(urlPaymentMin) : undefined,
    paymentMax: urlPaymentMax ? Number(urlPaymentMax) : undefined,
    priceMin: urlPriceMin ? Number(urlPriceMin) : undefined,
    priceMax: urlPriceMax ? Number(urlPriceMax) : undefined,
    searchTerm: debouncedSearch || undefined,
    // Multi-select sidebar inputs
    types: sidebarValues.types,
    features: sidebarValues.features,
    bedTypes: sidebarValues.bedTypes,
    manufacturers: sidebarValues.manufacturers,
    makes: sidebarValues.makes,
    years: sidebarValues.years.map((y) => Number(y)).filter((n) => !Number.isNaN(n)),
    paymentBuckets: sidebarValues.paymentBuckets,
    weightBuckets: sidebarValues.weightBuckets,
    lengthBuckets: sidebarValues.lengthBuckets,
    sleepsBuckets: sidebarValues.sleepsBuckets,
    priceBuckets: sidebarValues.priceBuckets,
  }), [condition, rvType, feature, sort, urlFeatured, urlSpecial, urlDealOfWeek, urlWeightMin, urlWeightMax, urlSleepsMin, urlSleepsMax, urlPaymentMin, urlPaymentMax, urlPriceMin, urlPriceMax, debouncedSearch, sidebarValues]);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePagedInventory(filters);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [userScrolled, setUserScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 200) setUserScrolled(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasNextPage || !userScrolled) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage();
    }, { rootMargin: "200px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, userScrolled]);

  // Flatten all loaded pages into one array
  const allUnits = useMemo(
    () => data?.pages.flatMap((p) => p.units) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  // Ranked keyword search across the full filtered set (in search mode the
  // hook returns all matching-filter units in one page, so ranking sees every
  // candidate and the displayed count is exact).
  const filtered = useMemo(() => {
    if (!debouncedSearch) return allUnits;
    return incomingUnitsLast(rankSearch(allUnits, debouncedSearch, (rv) => [
      { value: rv.stock_number, weight: 10 },
      { value: rv.rv_type, weight: 9 },
      { value: rv.title, weight: 8 },
      { value: rv.make, weight: 7 },
      { value: rv.model, weight: 7 },
      { value: rv.trim, weight: 6 },
      { value: rv.manufacturer, weight: 5 },
      { value: rv.year, weight: 5 },
      { value: rv.features, weight: 4 },
      { value: rv.floorplan_features, weight: 4 },
      { value: rv.special_label, weight: 3 },
      { value: rv.condition, weight: 2 },
      { value: rv.vin, weight: 2 },
      { value: rv.description, weight: 1 },
    ]));
  }, [allUnits, debouncedSearch]);

  // Never move the page while the visitor is typing — suggestions appear in
  // an anchored dropdown under the search box instead. Scrolling to the
  // results only happens on an explicit commit (Search button, Enter, or
  // the dropdown's "Show all" action).
  const resultsRef = useRef<HTMLElement | null>(null);
  const router = useRouter();
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const landAtResults = useCallback((behavior: ScrollBehavior = 'instant') => {
    const el = resultsRef.current;
    if (!el) return;
    const headerH = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
    window.scrollTo({ top: inventoryLandingTop(el.getBoundingClientRect().top, window.scrollY, headerH), behavior });
  }, []);

  const executeSearch = useCallback(() => {
    clearTimeout(debounceRef.current);
    setSuggestOpen(false);
    setDebouncedSearch(searchTerm);
    landAtResults('smooth');
  }, [searchTerm, landAtResults]);

  // Own the landing position after router commit, including unfiltered and
  // condition/type links. Do not tie this to typing/debounced filter updates.
  useEffect(() => {
    let id = 0;
    const land = () => {
      window.cancelAnimationFrame(id);
      id = window.requestAnimationFrame(() => {
      landAtResults();
    });
    };
    land();
    const unsubscribe = router.subscribe('onResolved', (event) => {
      if (landingIntent.current.shouldLand(event.fromLocation?.pathname, event.toLocation.pathname, event.toLocation.search)) land();
    });
    // Above-list widgets and the sticky menu may settle after the first frame.
    // Keep the landing aligned only until the visitor starts interacting.
    const observer = new ResizeObserver(land);
    const main = resultsRef.current?.closest('main');
    if (main) observer.observe(main);
    const header = document.querySelector('header');
    if (header) observer.observe(header);
    const stop = () => { observer.disconnect(); window.cancelAnimationFrame(id); };
    const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    events.forEach(event => window.addEventListener(event, stop, { once: true, passive: true }));
    const timeout = window.setTimeout(() => observer.disconnect(), 3000);
    return () => {
      unsubscribe(); stop(); window.clearTimeout(timeout);
      events.forEach(event => window.removeEventListener(event, stop));
    };
  }, [router, landAtResults]);

  const grouped = useMemo(() => {
    if (groupBy === "none") return null;
    const map = new Map<string, RVUnit[]>();
    for (const rv of filtered) {
      const key =
        rv.status === "coming_soon" ? "Coming Soon"
        : groupBy === "type" ? (rv.rv_type || "Other")
        : groupBy === "make" ? (rv.make || "Unknown")
        : (rv.condition === "new" ? "New" : rv.condition === "used" ? "Used" : rv.condition || "Other");
      const items = map.get(key) ?? [];
      items.push(rv);
      map.set(key, items);
    }
    return Array.from(map.entries()).sort((a, b) =>
      Number(a[0] === "Coming Soon") - Number(b[0] === "Coming Soon") || a[0].localeCompare(b[0])
    );
  }, [filtered, groupBy]);

  return (
    <Layout>
      <Helmet>
        {/* Title/description/canonical/og live in the rv-search route head();
            this Helmet only adds the CollectionPage JSON-LD. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "RV Inventory",
          description: "New and used RVs for sale at Rex & Sons RVs near Wilmington, NC.",
          url: "https://www.rexandsonsrvs.com/rv-search",
          isPartOf: { "@type": "WebSite", name: "Rex & Sons RVs", url: "https://www.rexandsonsrvs.com" },
          about: { "@type": "Thing", name: "Recreational Vehicles for Sale" },
        })}</script>
      </Helmet>
      {wizardOpen && (
        <Suspense fallback={null}>
          <RVExpertWizard open={wizardOpen} onOpenChange={setWizardOpen} />
        </Suspense>
      )}
      <WidgetZone zone="categorydetails_top" />
      <HeroSlider slides={heroSlides} height="min-h-[320px]">
        <div className="mx-auto flex max-w-4xl flex-wrap items-end gap-3 rounded-xl border bg-card p-4 shadow-lg">
          <div className="flex-1 min-w-[120px]">
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger aria-label="Condition"><SelectValue>{condition === "new" ? "New Only" : condition === "used" ? "Used Only" : "New & Used"}</SelectValue></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">New & Used</SelectItem>
                <SelectItem value="new">New Only</SelectItem>
                <SelectItem value="used">Used Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <Select value={rvType} onValueChange={setRvType}>
              <SelectTrigger aria-label="RV type"><SelectValue>{slugToLabel(rvType, rvTypes) ?? "All Types"}</SelectValue></SelectTrigger>
              <SelectContent>
                {rvTypes.map((t) => (
                  <SelectItem key={t} value={t.toLowerCase().replace(/\s+/g, "-")}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <Select value={feature} onValueChange={setFeature}>
              <SelectTrigger aria-label="Feature"><SelectValue>{slugToLabel(feature, floorplanFeatures) ?? "All Features"}</SelectValue></SelectTrigger>
              <SelectContent>
                {floorplanFeatures.map((f) => (
                  <SelectItem key={f} value={f.toLowerCase().replace(/\s+/g, "-")}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[140px]" ref={searchBoxRef}>
            <Input
              placeholder="Search make, model, type, or feature…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value.trim()) setSuggestOpen(true);
                else setSuggestOpen(false);
              }}
              onFocus={() => { if (searchTerm.trim()) setSuggestOpen(true); }}
              onKeyDown={(e) => {
                if (e.key === "Enter") executeSearch();
                else if (e.key === "Escape") setSuggestOpen(false);
              }}
              aria-label="Search inventory"
              role="combobox"
              aria-expanded={suggestOpen && !!debouncedSearch}
              aria-controls="inventory-search-suggestions"
              aria-autocomplete="list"
            />
          </div>
          <Button type="button" className="font-heading uppercase" onClick={executeSearch}>
            <Search className="mr-2 h-4 w-4" />Search
          </Button>
        </div>
      </HeroSlider>
      <SearchSuggestDropdown
        anchorRef={searchBoxRef as React.RefObject<HTMLElement>}
        open={suggestOpen && !!debouncedSearch}
        loading={isLoading}
        query={debouncedSearch}
        items={filtered.slice(0, 5)}
        totalCount={filtered.length}
        onShowAll={executeSearch}
        onClose={() => setSuggestOpen(false)}
      />

      {/* Results toolbar */}
      <section className="border-b bg-card">
        <div className="container flex flex-col gap-2 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex items-center justify-between gap-2">
            {debouncedSearch ? (
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{filtered.length}</span>{" "}
                {filtered.length === 1 ? "result" : "results"} for{" "}
                <span className="font-semibold text-foreground">“{debouncedSearch}”</span>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{totalCount}</span> {totalCount === 1 ? "unit" : "units"}
              </p>
            )}
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={resetSearch} className="h-7 text-xs gap-1.5">
                <X className="h-3 w-3" />Reset
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 sm:flex-none">
              <Select value={groupBy} onValueChange={(v: any) => setGroupBy(v)}>
                <SelectTrigger aria-label="Group by" className="w-full sm:w-[160px] h-9 text-xs">
                  <Layers className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                  <SelectValue>{({ type: "Group by Type", make: "Group by Make", condition: "Group by Condition", none: "No Grouping" })[groupBy]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="type">Group by Type</SelectItem>
                  <SelectItem value="make">Group by Make</SelectItem>
                  <SelectItem value="condition">Group by Condition</SelectItem>
                  <SelectItem value="none">No Grouping</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-0 sm:flex-none">
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger aria-label="Sort by" className="w-full sm:w-[180px] h-9 text-xs">
                  <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                  <SelectValue>{sortOptions.find((option) => option.value === sort)?.label ?? "Featured"}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="hidden sm:flex border rounded-md">
              <button type="button" aria-label="Grid view" onClick={() => setViewMode("grid")} className={`p-1.5 ${viewMode === "grid" ? "bg-muted" : ""}`}><Grid3X3 className="h-4 w-4" /></button>
              <button type="button" aria-label="List view" onClick={() => setViewMode("list")} className={`p-1.5 ${viewMode === "list" ? "bg-muted" : ""}`}><List className="h-4 w-4" /></button>
            </div>
            {autoCycleSetting?.enabled && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAutoCyclePaused((p) => !p)}
                className="hidden sm:flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition hover:bg-muted"
                title={autoCyclePaused ? "Resume image cycling" : "Pause image cycling"}
                aria-label={autoCyclePaused ? "Resume image cycling" : "Pause image cycling"}
                aria-pressed={autoCyclePaused}
              >
                {autoCyclePaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                <span>{autoCyclePaused ? "Play" : "Pause"} Photos</span>
              </Button>
            )}
          </div>
        </div>
      </section>
      <WidgetZone zone="categorydetails_before_filters" />

      {/* Shop By filters — collapsible */}
      <section className="py-4 bg-muted">
        <div className="container">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="w-full flex items-center justify-center gap-2 py-2"
          >
            <h2 className="font-heading text-2xl font-bold uppercase text-foreground">Shop With <span className="text-primary">RV Filters</span></h2>
            <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
          </button>
          {filtersOpen && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-4">
              <div className="rounded-xl border bg-card p-4 shadow-xs">
                <h3 className="font-heading text-sm font-bold uppercase text-primary mb-2">By Floorplan</h3>
                <div className="flex flex-wrap gap-1.5">
                  {["Bunkhouse", "Front Bedroom", "Outdoor Kitchen", "Kitchen Island", "Front Living", "Rear Living Area", "Rear Bath", "Murphy Bed"].map((f) => (
                    <button key={f} onClick={() => { setFeature(f.toLowerCase().replace(/\s+/g, "-")); }} className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition hover:bg-primary hover:text-primary-foreground">{f}</button>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border bg-card p-4 shadow-xs">
                <h3 className="font-heading text-sm font-bold uppercase text-primary mb-2">By Sleeping</h3>
                <div className="flex flex-wrap gap-1.5">
                  {[{l:"Sleeps 2–4",p:"sleeps_min=2&sleeps_max=4"},{l:"Sleeps 5–6",p:"sleeps_min=5&sleeps_max=6"},{l:"Sleeps 7–8",p:"sleeps_min=7&sleeps_max=8"},{l:"Sleeps 9+",p:"sleeps_min=9"}].map((s) => (
                    <Link key={s.p} to={`/rv-search?${s.p}`} className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition hover:bg-primary hover:text-primary-foreground">{s.l}</Link>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border bg-card p-4 shadow-xs">
                <h3 className="font-heading text-sm font-bold uppercase text-primary mb-2">By Weight</h3>
                <div className="flex flex-wrap gap-1.5">
                  {[{l:"3,500 & Under",p:"weight_max=3500"},{l:"3,500–5,500",p:"weight_min=3500&weight_max=5500"},{l:"5,500–8,500",p:"weight_min=5500&weight_max=8500"},{l:"8,500–11,000",p:"weight_min=8500&weight_max=11000"},{l:"11,000–14,000",p:"weight_min=11000&weight_max=14000"},{l:"14,000+",p:"weight_min=14000"}].map((w) => (
                    <Link key={w.p} to={`/rv-search?${w.p}`} className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition hover:bg-primary hover:text-primary-foreground">{w.l} lbs</Link>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border bg-card p-4 shadow-xs">
                <h3 className="font-heading text-sm font-bold uppercase text-primary mb-2">By Payment</h3>
                <div className="flex flex-wrap gap-1.5">
                  {[{l:"Under $199",p:"payment_max=199"},{l:"$200–$299",p:"payment_min=200&payment_max=299"},{l:"$300–$399",p:"payment_min=300&payment_max=399"},{l:"$400+",p:"payment_min=400"}].map((pm) => (
                    <Link key={pm.p} to={`/rv-search?${pm.p}`} className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition hover:bg-primary hover:text-primary-foreground">{pm.l}/mo</Link>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border bg-card p-4 shadow-xs sm:col-span-2 lg:col-span-4">
                <h3 className="font-heading text-sm font-bold uppercase text-primary mb-2">By Status</h3>
                <div className="flex flex-wrap gap-1.5">
                  <Link to="/rv-search?featured=true" className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition hover:bg-primary hover:text-primary-foreground">⭐ Featured</Link>
                  <Link to="/rv-search?special=true" className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition hover:bg-primary hover:text-primary-foreground">🏷️ Specials</Link>
                  <Link to="/rv-search?special=deal-of-week" className="rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground transition hover:bg-primary hover:text-primary-foreground">🔥 Deal of the Week</Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      {/* Active status filter label */}
      {(urlFeatured === "true" || urlSpecial === "true" || urlSpecial === "deal-of-week" || urlSpecial === "manager") && (
        <div className="container pt-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary">
            {urlFeatured === "true" && "⭐ Showing Featured Units"}
            {urlSpecial === "true" && "🏷️ Showing Specials"}
            {urlSpecial === "deal-of-week" && "🔥 Showing Deal of the Week"}
            {urlSpecial === "manager" && "🏷️ Showing Manager Specials"}
            <Button type="button" variant="ghost" size="icon" aria-label="Clear status filter" onClick={resetSearch} className="ml-1 min-h-11 min-w-11 hover:text-primary/70"><X className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}
      <RVExpertBanner onStart={() => setWizardOpen(true)} />
      <WidgetZone zone="categorydetails_before_Unit_list" />

      {/* Inventory grid */}
      <section id="inventory-results" aria-label="RV inventory results" className="py-12" ref={resultsRef}>
        <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-8">
          {/* Mobile filter trigger */}
          <div className="lg:hidden mb-4">
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="w-full font-heading uppercase gap-2">
                  <SlidersHorizontal className="h-4 w-4" />
                  Filters {sidebarSelectedCount > 0 && <span className="text-primary">({sidebarSelectedCount})</span>}
                </Button>
              </SheetTrigger>
              {mobileFiltersOpen && <SheetContent side="left" className="w-[88vw] sm:w-[420px] overflow-y-auto" aria-describedby={undefined}>
                <SheetTitle className="sr-only">Inventory filters</SheetTitle>
                <div className="pt-6">
                  <InventoryFilterSidebar
                    values={sidebarValues}
                    onChange={handleSidebarChange}
                    onClear={handleSidebarClear}
                  />
                </div>
              </SheetContent>}
            </Sheet>
          </div>

          <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-6">
            {/* Desktop sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-2">
                <InventoryFilterSidebar
                  values={sidebarValues}
                  onChange={handleSidebarChange}
                  onClear={handleSidebarClear}
                />
              </div>
            </div>

            <div className="min-w-0">
          {isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[4/5] rounded-xl" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            debouncedSearch ? (
              <div className="mx-auto max-w-lg text-center py-12">
                <h3 className="font-heading text-xl font-bold text-foreground">
                  No matches for “{debouncedSearch}”
                </h3>
                <p className="mt-2 text-muted-foreground">
                  Try another keyword — or browse what's in stock by type:
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-1.5">
                  {rvTypes.filter((t) => t !== "All Types").map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        setDebouncedSearch("");
                        setRvType(t.toLowerCase().replace(/\s+/g, "-"));
                      }}
                      className="rounded-full border px-3 py-1 text-xs text-muted-foreground transition hover:bg-primary hover:text-primary-foreground"
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <Button onClick={() => setWizardOpen(true)} className="mt-6 font-heading uppercase">
                  Let Us Find It For You
                </Button>
              </div>
            ) : (
              <div className="mx-auto max-w-md text-center py-12">
                <h3 className="font-heading text-xl font-bold text-foreground">No Units Found</h3>
                <p className="mt-2 text-muted-foreground">Try adjusting your search filters or browse all inventory.</p>
                <Button onClick={resetSearch} variant="outline" className="mt-4 font-heading uppercase">Clear Filters</Button>
              </div>
            )
          ) : (
            <AutoCyclePausedProvider value={autoCyclePaused}>
              {grouped ? (
                <div className="space-y-10">
                  {grouped.map(([groupLabel, items], gi) => (
                    <div key={groupLabel}>
                      <h3 className="font-heading text-xl font-bold uppercase text-foreground mb-4 border-b border-border pb-2">
                        {groupLabel} <span className="text-sm font-normal text-muted-foreground">({items.length})</span>
                      </h3>
                      <div className={viewMode === "grid" ? "grid gap-6 sm:grid-cols-2 xl:grid-cols-3" : "space-y-4"}>
                        {items.map((rv, i) => (
                          viewMode === "list" ? (
                            <HorizontalRowCard key={rv.id} rv={rv} priority={gi === 0 && i === 0} />
                          ) : (
                            <RVCard key={rv.id} rv={rv} priority={gi === 0 && i === 0} />
                          )
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={viewMode === "grid" ? "grid gap-6 sm:grid-cols-2 xl:grid-cols-3" : "space-y-4"}>
                  {filtered.map((rv, i) => (
                    viewMode === "list" ? (
                      <HorizontalRowCard key={rv.id} rv={rv} priority={i === 0} />
                    ) : (
                      <RVCard key={rv.id} rv={rv} priority={i === 0} />
                    )
                  ))}
                </div>
              )}
              {hasNextPage && (
                <div ref={loadMoreRef} className="mt-8 flex justify-center">
                  <Button
                    variant="outline"
                    size="lg"
                    className="font-heading uppercase"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? "Loading…" : `Show More (${totalCount - allUnits.length > 0 ? totalCount - allUnits.length : "more"} remaining)`}
                  </Button>
                </div>
              )}
            </AutoCyclePausedProvider>
          )}
            </div>
          </div>
        </div>
      </section>
      <WidgetZone zone="categorydetails_bottom" />
    </Layout>
  );
};

export default Inventory;
